import { useEffect, useMemo } from "react";
import {
  Page,
  StatCard,
  ClinicFilterBar,
  PayPeriod,
  getDefaultPayPeriodValue,
  getDefaultPayrollMonth,
  getCurrentMonth,
  usePersistedState,
} from "@common";
import { useAuth } from "@api/useAuth";
import {
  isOrganizerOrAdmin,
  isObserver,
  isAreaManager,
  isPractitionerLead,
  isFinanceCoordinator,
  torontoToday,
  addDaysISO,
  getStoredAssociateFeePageSize,
} from "@utils";
import useClinicFilterOptions from "@hooks/useClinicFilterOptions";
import FinanceOperation from "@pages/admin/FinanceOperation";

// Daily Deposits opens on this many days, ending today; any range can be picked.
const DAILY_DEPOSITS_DEFAULT_DAYS = 30;

/**
 * Standalone Finance Operations page.
 *
 * A top-level section (like Cash Flow) that hosts the finance sub-tabs
 * (Associate Fee, Journal Entry, Check, Daily Deposits, Deposit Sheet, ADP Netsuite, Cash
 * Management). Previously these lived as a tab inside both Admin (Home) and
 * Management (Home); this consolidates them in one place.
 *
 * Clinic dropdown conditions by role:
 *   - Organizer, Admin, Observer, Payroll, Bookkeeper, Finance Coordinator:
 *     see ALL clinics, plus the Area Manager filter.
 *   - Area Manager, Practitioner Lead: see ONLY their assigned clinics
 *     (user.clinic); the Area Manager filter is hidden.
 * Write access within each sub-tab is still gated by that tab's own role checks.
 *
 * Visible to: Organizer, Admin, Area Manager, Observer, Payroll, Bookkeeper,
 * Finance Coordinator, and Practitioner Lead — who gets the Daily Deposits tab
 * only, read-only (everyone except Visitor).
 */
export default function FinanceOperationsPage() {
  const { user } = useAuth();

  // ADP Netsuite sub-tab: admin-scope roles plus Finance Coordinator, which
  // posts the ADP journal entries (its write is can_act_on_payroll — role 9).
  const canSeeAdpNetsuite =
    isOrganizerOrAdmin(user) || isObserver(user) || isFinanceCoordinator(user);
  // Area Manager and Practitioner Lead are the clinic-scoped roles on this page:
  // they're limited to their assigned clinics. Everyone else is an all-clinic
  // viewer. Practitioner Lead only gets Daily Deposits.
  const isLead = isPractitionerLead(user);
  const isClinicScoped = isAreaManager(user) || isLead;

  const [financeTab, setFinanceTab] = usePersistedState("finance:financeTab", "associateFee");
  const [months, setMonths] = usePersistedState("finance:months", () => [getDefaultPayrollMonth()]);
  const [cashDate, setCashDate] = usePersistedState("finance:cashDate", () => torontoToday());
  // Daily Deposits: a free from / to range, by default the last
  // DAILY_DEPOSITS_DEFAULT_DAYS ending today. Never past today (the picker's max).
  const defaultDepositsRange = {
    from: addDaysISO(torontoToday(), -(DAILY_DEPOSITS_DEFAULT_DAYS - 1)),
    to: torontoToday(),
  };
  const [dailyDepositsRange, setDailyDepositsRange] = usePersistedState(
    "finance:dailyDepositsRange",
    () => defaultDepositsRange,
  );
  const [areaManagerFilter, setAreaManagerFilter] = usePersistedState(
    "finance:areaManagerFilter",
    "",
  );
  const [clinicFilters, setClinicFilters] = usePersistedState("finance:clinicFilters", []);
  const [payPeriod, setPayPeriod] = usePersistedState("finance:payPeriod", () =>
    getDefaultPayPeriodValue(),
  );
  const [page, setPage] = usePersistedState("finance:page", 1);
  const [associateFeePageSize, setAssociateFeePageSize] = usePersistedState(
    "finance:associateFeePageSize",
    getStoredAssociateFeePageSize,
  );

  const {
    clinicsQuery,
    areaManagerOptions: allAreaManagerOptions,
    clinicOptions: allClinicOptions,
  } = useClinicFilterOptions(areaManagerFilter);

  // Per-role dropdown conditions:
  //   - Area Manager: only their assigned clinics; no Area Manager filter.
  //   - Everyone else: all clinics + Area Manager filter.
  const clinicOptions = useMemo(() => {
    if (!isClinicScoped) return allClinicOptions;
    const allowed = new Set((user?.clinic || []).map((c) => String(c).toUpperCase()));
    return allClinicOptions.filter((o) => allowed.has(String(o.value).toUpperCase()));
  }, [isClinicScoped, allClinicOptions, user?.clinic]);

  const areaManagerOptions = isClinicScoped ? [] : allAreaManagerOptions;

  // Resolved clinic list. An empty picker means NOTHING is selected rather than
  // everything: these tabs fan their queries out per clinic, so defaulting to
  // the full list made opening the page the most expensive thing it does.
  //
  // Two selections that aren't the clinic picker still resolve to a real list:
  // an area manager narrows `clinicOptions` to their clinics, and an
  // area-manager user is permanently scoped to theirs. Both are a choice
  // already made, so neither should land on an empty page.
  const activeClinics = useMemo(() => {
    if (clinicFilters.length > 0) return clinicFilters;
    if (areaManagerFilter || isClinicScoped) return clinicOptions.map((o) => o.value);
    return [];
  }, [clinicFilters, clinicOptions, areaManagerFilter, isClinicScoped]);

  const financeTabOptions = useMemo(
    () =>
      isLead
        ? [{ key: "dailyDeposits", label: "Daily Deposits" }]
        : [
            { key: "associateFee", label: "Associate Fee" },
            { key: "journalEntry", label: "Journal Entry" },
            { key: "check", label: "Check" },
            { key: "dailyDeposits", label: "Daily Deposits" },
            { key: "depositSheet", label: "Deposit Sheet" },
            ...(canSeeAdpNetsuite ? [{ key: "adpNetsuite", label: "ADP Netsuite" }] : []),
            { key: "cashManagement", label: "Cash Management" },
          ],
    [canSeeAdpNetsuite, isLead],
  );

  // Keep the selected sub-tab valid if the option set changes (e.g. an
  // admin-scope role loses ADP Netsuite).
  useEffect(() => {
    if (!financeTabOptions.some((o) => o.key === financeTab)) {
      setFinanceTab(financeTabOptions[0]?.key ?? "associateFee");
    }
  }, [financeTabOptions, financeTab, setFinanceTab]);

  // Deposit Sheet and Daily Deposits may look at the current month; other
  // month-aware tabs clamp to the last completed payroll month via the picker's
  // disableCurrentAndFuture.
  const isDepositSheet = financeTab === "depositSheet";
  const isCashManagement = financeTab === "cashManagement";
  const isAdp = financeTab === "adpNetsuite";
  const isDailyDeposits = financeTab === "dailyDeposits";

  const allowsCurrentMonth = isDepositSheet;

  // Deposit Sheet defaults to the current calendar month rather than the
  // last-completed payroll default the other tabs share.
  useEffect(() => {
    if (!allowsCurrentMonth) return;
    if (months.length === 1 && months[0] === getDefaultPayrollMonth()) {
      setMonths([getCurrentMonth()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowsCurrentMonth]);

  // Only Deposit Sheet takes several months; the other tabs read one, so
  // collapse a multi-month selection back to its first month on the way out.
  useEffect(() => {
    if (!isDepositSheet && months.length > 1) setMonths([months[0]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDepositSheet]);

  useEffect(() => {
    setPage(1);
  }, [months, clinicFilters, setPage]);

  // The month / clinic (or pay-period) selection bar. Rendered by
  // FinanceOperation *below* the sub-tab bar via the selectionSlot prop.
  const selectionBar = isAdp ? (
    <div className="mb-4">
      <StatCard icon="📅" label="Pay Period">
        <div className="mt-2">
          <PayPeriod onChange={setPayPeriod} value={payPeriod} />
        </div>
      </StatCard>
    </div>
  ) : isDailyDeposits ? (
    <ClinicFilterBar
      rangeFrom={dailyDepositsRange.from}
      rangeTo={dailyDepositsRange.to}
      rangeMax={torontoToday()}
      onRangeChange={setDailyDepositsRange}
      onRangeReset={() => setDailyDepositsRange(defaultDepositsRange)}
      rangeIsDefault={
        dailyDepositsRange.from === defaultDepositsRange.from &&
        dailyDepositsRange.to === defaultDepositsRange.to
      }
      areaManagerFilter={areaManagerFilter}
      areaManagerOptions={areaManagerOptions}
      onAreaManagerChange={(v) => {
        setAreaManagerFilter(v);
        setClinicFilters([]);
      }}
      clinicOptions={clinicOptions}
      clinicValue={clinicFilters}
      onClinicChange={setClinicFilters}
      singleClinic
    />
  ) : isCashManagement ? (
    <ClinicFilterBar
      dateValue={cashDate}
      onDateChange={setCashDate}
      areaManagerFilter={areaManagerFilter}
      areaManagerOptions={areaManagerOptions}
      onAreaManagerChange={(v) => {
        setAreaManagerFilter(v);
        setClinicFilters([]);
      }}
      clinicOptions={clinicOptions}
      clinicValue={clinicFilters}
      onClinicChange={setClinicFilters}
    />
  ) : (
    <ClinicFilterBar
      months={months}
      onMonthsChange={setMonths}
      disableCurrentAndFuture={!allowsCurrentMonth}
      multipleMonths={isDepositSheet}
      areaManagerFilter={areaManagerFilter}
      areaManagerOptions={areaManagerOptions}
      onAreaManagerChange={(v) => {
        setAreaManagerFilter(v);
        setClinicFilters([]);
      }}
      clinicOptions={clinicOptions}
      clinicValue={clinicFilters}
      onClinicChange={setClinicFilters}
    />
  );

  return (
    <Page>
      <FinanceOperation
        selectionSlot={selectionBar}
        financeTab={financeTab}
        setFinanceTab={setFinanceTab}
        financeTabOptions={financeTabOptions}
        months={months}
        payPeriod={payPeriod}
        cashDate={cashDate}
        dailyDepositsRange={dailyDepositsRange}
        page={page}
        setPage={setPage}
        associateFeePageSize={associateFeePageSize}
        setAssociateFeePageSize={setAssociateFeePageSize}
        associateFeeClinics={clinicFilters}
        associateFeeClinicCodes={
          clinicFilters.length === 0 && areaManagerFilter ? activeClinics : null
        }
        journalEntryClinics={activeClinics}
        checkClinics={activeClinics}
        // One clinic at most, and only an explicit pick: never the area-manager
        // expansion `activeClinics` does. The query load scales per clinic.
        // The shared selection itself is left alone so other tabs keep theirs.
        dailyDepositsClinics={clinicFilters.slice(0, 1)}
        depositSheetClinics={activeClinics}
        cashManagementClinics={activeClinics}
        cashManagementClinicOptions={clinicOptions}
        canSeeJournalEntry
        canSeeCheck
        canSeeDailyDeposits
        canSeeDepositSheet
        canSeeAdpNetsuite={canSeeAdpNetsuite}
      />

      {clinicsQuery.isError && (
        <p className="mt-4 text-sm text-palette-rose-heading">Failed to load clinics.</p>
      )}
    </Page>
  );
}
